import { useMoneyFormatter } from "@/lib/money-format";
/**
 * /companies/:companyId/reportes/balance-general
 * Balance General (balance sheet) view.
 */
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Printer, Scale } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { BalanceSheetView } from "@/components/reports/BalanceSheetView";
import { PrintableReport } from "@/components/reports/PrintableReport";
import { CsvExportButton } from "@/components/reports/CsvExportButton";
import { useActiveCompany } from "@/lib/company-context";
import { useBalanceSheet } from "@/lib/api/reports.queries";

export const Route = createFileRoute("/companies/$companyId/reportes/balance-general")({
  component: BalanceGeneralPage,
});

function BalanceGeneralPage() {
  return (
    <RequireAuth>
      <AppShell>
        <BalanceGeneralContent />
      </AppShell>
    </RequireAuth>
  );
}

function BalanceGeneralContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const [asOf, setAsOf] = useState<string>(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const { data: report, isLoading, isError, refetch } = useBalanceSheet(companyId, asOf);

  const formatMoney = useMoneyFormatter("decimal");

  const csvRows = report
    ? [
        ...report.assets.items.map((i) => ({ seccion: "Activos", cuenta: i.accountCode, nombre: i.accountName, monto: formatMoney(i.amount) })),
        ...report.liabilities.items.map((i) => ({ seccion: "Pasivos", cuenta: i.accountCode, nombre: i.accountName, monto: formatMoney(i.amount) })),
        ...report.equity.items.map((i) => ({ seccion: "Patrimonio", cuenta: i.accountCode, nombre: i.accountName, monto: formatMoney(i.amount) })),
      ]
    : [];

  return (
    <PrintableReport
      title={t("reports.bs.title")}
      subtitle={report ? `${t("reports.bs.as_of")}: ${report.asOf}` : undefined}
    >
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-blue-600" />
            <h1 className="text-xl font-bold">{t("reports.bs.title")}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <CsvExportButton
              filename={`balance-general-${asOf}`}
              rows={csvRows}
              headers={[
                { key: "seccion", label: "Seccion" },
                { key: "cuenta", label: "Codigo" },
                { key: "nombre", label: "Nombre" },
                { key: "monto", label: "Monto COP" },
              ]}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="print:hidden"
              onClick={() => window.print()}
            >
              <Printer className="mr-2 h-4 w-4" />
              {t("reports.print")}
            </Button>
          </div>
        </div>

        {/* Date picker */}
        <div className="flex items-center gap-3 print:hidden">
          <label className="text-sm font-medium">{t("reports.bs.as_of")}:</label>
          <Input
            type="date"
            className="h-8 w-40 text-sm"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        ) : isError ? (
          <ErrorState
            title={t("reports.error.title")}
            description={t("reports.error.body")}
            onRetry={() => void refetch()}
          />
        ) : report ? (
          <BalanceSheetView report={report} />
        ) : null}
      </div>
    </PrintableReport>
  );
}
